// Mobile port of the web TKNE Configuration editor (Settings → TKNE →
// Design Groups / Basic Project Templates). Reads/writes
// user_settings.design_groups and user_settings.basic_templates directly —
// same shape as web's DesignGroup / BasicTemplate (src/interfaces/settings.ts).
import React, { useState } from 'react';
import { Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GoldButton, GlassCard } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

interface DesignGroup {
  id: string;
  name: string;
}

interface BasicTemplateRow {
  id: string;
  label: string;
  prompt: string;
}

interface BasicTemplate {
  id: string;
  name: string;
  rows: BasicTemplateRow[];
}

export default function TkneEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value: designGroups, setValue: setDesignGroups, loading: groupsLoading } = useSettingColumn<DesignGroup[]>(
    userId,
    'design_groups',
    []
  );
  const { value: templates, setValue: setTemplates, loading: templatesLoading } = useSettingColumn<BasicTemplate[]>(
    userId,
    'basic_templates',
    []
  );
  const [newGroupName, setNewGroupName] = useState('');

  const addDesignGroup = () => {
    const name = newGroupName.trim() || 'New Group';
    setDesignGroups([...designGroups, { id: `dg-${Date.now()}`, name }]);
    setNewGroupName('');
  };

  const updateDesignGroup = (id: string, name: string) => {
    setDesignGroups(designGroups.map((g) => (g.id === id ? { ...g, name } : g)));
  };

  const deleteDesignGroup = (id: string) => {
    setDesignGroups(designGroups.filter((g) => g.id !== id));
  };

  const updateTemplate = (id: string, patch: Partial<BasicTemplate>) => {
    setTemplates(templates.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  const addTemplate = () => {
    const template: BasicTemplate = {
      id: `tpl-${Date.now()}`,
      name: 'New Template',
      rows: [
        { id: `r-${Date.now()}-1`, label: 'Section 1', prompt: '' },
        { id: `r-${Date.now()}-2`, label: 'Section 2', prompt: '' },
        { id: `r-${Date.now()}-3`, label: 'Section 3', prompt: '' },
      ],
    };
    setTemplates([...templates, template]);
  };

  const deleteTemplate = (id: string) => {
    Alert.alert('Delete template?', 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => setTemplates(templates.filter((t) => t.id !== id)),
      },
    ]);
  };

  const addRow = (template: BasicTemplate) => {
    updateTemplate(template.id, {
      rows: [...template.rows, { id: `r-${Date.now()}`, label: '', prompt: '' }],
    });
  };

  const updateRow = (template: BasicTemplate, rowId: string, patch: Partial<BasicTemplateRow>) => {
    updateTemplate(template.id, {
      rows: template.rows.map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
    });
  };

  const deleteRow = (template: BasicTemplate, rowId: string) => {
    if (template.rows.length <= 1) return;
    updateTemplate(template.id, { rows: template.rows.filter((r) => r.id !== rowId) });
  };

  if (groupsLoading || templatesLoading) {
    return (
      <View style={styles.container}>
        <Text style={styles.hint}>Loading…</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SectionTitle style={styles.sectionGap}>ORDER Groups</SectionTitle>
      {designGroups.length === 0 ? <Text style={styles.hint}>No ORDER groups yet.</Text> : null}

      {designGroups.map((group) => (
        <View key={group.id} style={styles.row}>
          <Field style={styles.flex1}
            inputStyle={[styles.input, styles.flex1]}
            value={group.name}
            onChangeText={(t) => updateDesignGroup(group.id, t)}
            placeholder="Group name"
          />
          <Pressable style={styles.deleteBtn} onPress={() => deleteDesignGroup(group.id)}>
            <Text style={styles.deleteText}>✕</Text>
          </Pressable>
        </View>
      ))}

      <View style={styles.row}>
        <Field style={styles.flex1}
          inputStyle={[styles.input, styles.flex1]}
          value={newGroupName}
          onChangeText={setNewGroupName}
          placeholder="New group name"
        />
        <GoldButton label="Add" onPress={addDesignGroup} style={styles.actionGap} />
      </View>

      <SectionTitle style={styles.sectionGap}>Basic Project Templates</SectionTitle>
      {templates.length === 0 ? <Text style={styles.hint}>No templates yet.</Text> : null}

      {templates.map((template) => (
        <GlassCard key={template.id} style={styles.card}>
          <View style={styles.row}>
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              value={template.name}
              onChangeText={(t) => updateTemplate(template.id, { name: t })}
              placeholder="Template name"
            />
          </View>

          {template.rows.map((row) => (
            <GlassCard key={row.id} style={styles.subCard}>
              <Field
                inputStyle={styles.input}
                value={row.label}
                onChangeText={(t) => updateRow(template, row.id, { label: t })}
                placeholder="Row label"
              />
              <View style={styles.row}>
                <Field style={styles.flex1}
                  inputStyle={[styles.input, styles.flex1]}
                  value={row.prompt}
                  onChangeText={(t) => updateRow(template, row.id, { prompt: t })}
                  placeholder="Prompt"
                  multiline
                />
                <Pressable
                  style={[styles.deleteBtn, template.rows.length <= 1 && styles.deleteBtnDisabled]}
                  onPress={() => deleteRow(template, row.id)}
                  disabled={template.rows.length <= 1}
                >
                  <Text style={styles.deleteText}>✕</Text>
                </Pressable>
              </View>
            </GlassCard>
          ))}

          <GoldButton label="+ Add Row" onPress={() => addRow(template)} style={styles.actionGap} />

          <Pressable style={styles.dangerButton} onPress={() => deleteTemplate(template.id)}>
            <Text style={styles.dangerText}>Delete Template</Text>
          </Pressable>
        </GlassCard>
      ))}

      <GoldButton label="+ Add Template" onPress={addTemplate} style={styles.actionGap} />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  container: { gap: 8 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  card: {
    padding: 12,
    marginTop: 10,
    gap: 8,
  },
  subCard: {
    padding: 10,
    gap: 8,
    marginTop: 6,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 },
  flex1: { flex: 1 },
  input: {
  },
  deleteBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  deleteBtnDisabled: { opacity: 0.3 },
  deleteText: { color: t.lapsed, fontWeight: '900' },
  dangerButton: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 4,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  dangerText: { color: t.lapsed, fontWeight: '800' },
}),
);
